import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { translations as en } from '../translations/en';
import { translations as es } from '../translations/es';

const translations = { en, es };

const Resources = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const resources = [
    {
      title: t.resources.faqTitle,
      description: t.resources.faqDescription,
      icon: '❓',
      link: '/faq'
    },
    {
      title: t.resources.topVenuesTitle,
      description: t.resources.topVenuesDescription,
      icon: '🏰',
      link: '/resources/guides/top-venues'
    },
    {
      title: t.resources.destinationGuidesTitle,
      description: t.resources.destinationGuidesDescription,
      icon: '📋',
      link: '/destinations'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Image */}
      <div className="relative h-[40vh] min-h-[400px]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1519741497674-611481863552?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80")'
          }}
        >
          <div className="absolute inset-0 bg-black opacity-40"></div>
        </div>
        <div className="relative h-full flex items-center">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                {t.resources.pageTitle}
              </h1>
              <p className="text-xl md:text-2xl text-white max-w-3xl mx-auto">
                {t.resources.pageDescription}
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Resources Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {resources.map((resource, index) => (
            <Link
              key={index}
              to={resource.link}
              className="group h-full"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border border-[#ececec] hover:border-[#bfa76a] transition-all duration-300 h-full flex flex-col"
              >
                <div className="text-3xl md:text-4xl mb-3 md:mb-4">{resource.icon}</div>
                <h2 className="text-xl md:text-2xl font-playfair font-bold mb-2 text-[#181c24]">
                  {resource.title}
                </h2>
                <p className="text-sm md:text-base text-[#888] flex-grow">
                  {resource.description}
                </p>
              </motion.div>
            </Link>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-[#C5A572] py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {t.resources.ctaTitle}
            </h2>
            <p className="text-xl text-white mb-8 max-w-2xl mx-auto">
              {t.resources.ctaDescription}
            </p>
            <Link
              to="/contact"
              className="inline-block bg-white text-[#C5A572] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors duration-300"
            >
              {t.resources.ctaButton}
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Resources;
// Note: Add the needed translation keys to en.js and es.js under a 'resources' section. 